import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Trash2,
  Download,
  Globe,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calculator,
  Calendar,
  FileText,
  BarChart3,
  Brain,
  MessageSquare,
  RefreshCw,
  AlertTriangle,
  Shield,
  CheckCircle,
  HelpCircle,
  MoreHorizontal,
  ArrowUpRight,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import { multiCurrencyService } from '../../services/multiCurrencyService';

// Custom Dropdown Component for Currency Actions
const CurrencyActionDropdown = ({ currency, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 200;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (currency && (currency.id || currency._id)) {
      onView(currency);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (currency && (currency.id || currency._id)) {
      onEdit(currency);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (currency && (currency.id || currency._id)) {
      onDelete(currency);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen &&
        (() => {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuMinWidth = 180;
          const left = Math.min(
            rect.left,
            window.innerWidth - menuMinWidth - 16,
          );
          const top = rect.bottom + 8;
          const bottom = window.innerHeight - rect.top + 8;

          const menuStyle = {
            position: 'fixed',
            top: openUpward ? undefined : `${top}px`,
            bottom: openUpward ? `${bottom}px` : undefined,
            left: `${left}px`,
            zIndex: 100000,
            minWidth: `${menuMinWidth}px`,
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '0',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
          };

          return createPortal(
            <ul
              className="dropdown-menu dropdown-menu-end show"
              style={menuStyle}
            >
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleView}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#374151',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Eye
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  View Currency
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleEdit}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#374151',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Edit
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  Edit Currency
                </button>
              </li>
              {currency?.status !== 'Base' && (
                <li
                  style={{
                    borderTop: '1px solid #e5e7eb',
                    marginTop: '4px',
                    paddingTop: '4px',
                  }}
                >
                  <button
                    className="dropdown-item d-flex align-items-center"
                    onClick={handleDelete}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      color: '#dc2626',
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#fef2f2';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Trash2
                      size={16}
                      style={{
                        marginRight: '10px',
                        color: '#dc2626',
                        flexShrink: 0,
                      }}
                    />
                    Delete Currency
                  </button>
                </li>
              )}
            </ul>,
            document.body,
          );
        })()}
    </div>
  );
};

// Tax rules data with country information, flags, and links
const TAX_RULES_DATA = [
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    link: 'https://www.irs.gov/businesses/international-businesses',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    link: 'https://www.gov.uk/tax-foreign-income',
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    currency: 'CAD',
    link: 'https://www.canada.ca/en/revenue-agency/services/tax/international-non-residents.html',
  },
  {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    link: 'https://www.ato.gov.au/Business/International-tax-for-business/',
  },
  {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪',
    currency: 'EUR',
    link: 'https://www.bundesfinanzministerium.de/Web/EN/Topics/Taxation/Taxation.html',
  },
  {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷',
    currency: 'EUR',
    link: 'https://www.impots.gouv.fr/international-press',
  },
  {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹',
    currency: 'EUR',
    link: 'https://www.agenziaentrate.gov.it/portale/web/english',
  },
  {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸',
    currency: 'EUR',
    link: 'https://www.agenciatributaria.es/AEAT.internet/en_gb/Inicio.shtml',
  },
  {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱',
    currency: 'EUR',
    link: 'https://www.belastingdienst.nl/wps/wcm/connect/bldcontenten/belastingdienst/individual/international',
  },
  {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪',
    currency: 'EUR',
    link: 'https://finance.belgium.be/en/international',
  },
  {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭',
    currency: 'CHF',
    link: 'https://www.admin.ch/gov/en/start/federal-council/tasks/federal-departments/department-of-finance-fdf.html',
  },
  {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪',
    currency: 'SEK',
    link: 'https://www.skatteverket.se/omoss/otherlanguages/inenglish.4.709b8ce41237eb4566f80f5.html',
  },
  {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴',
    currency: 'NOK',
    link: 'https://www.skatteetaten.no/en/person/foreign/',
  },
  {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰',
    currency: 'DKK',
    link: 'https://www.skm.dk/international',
  },
  {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮',
    currency: 'EUR',
    link: 'https://www.vero.fi/en/individuals/international-individuals/',
  },
  {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    currency: 'JPY',
    link: 'https://www.nta.go.jp/english/taxes/index.html',
  },
  {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳',
    currency: 'CNY',
    link: 'http://www.chinatax.gov.cn/eng/',
  },
  {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    link: 'https://www.incometax.gov.in/iec/foportal',
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    link: 'https://www.iras.gov.sg/taxes/individual-income-tax',
  },
  {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰',
    currency: 'HKD',
    link: 'https://www.ird.gov.hk/eng/tax/bus_pft.htm',
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    link: 'https://www.mof.gov.ae/en/Pages/default.aspx',
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    currency: 'SAR',
    link: 'https://zatca.gov.sa/en',
  },
  {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷',
    currency: 'BRL',
    link: 'https://www.gov.br/receitafederal/en',
  },
  {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽',
    currency: 'MXN',
    link: 'https://www.sat.gob.mx/english',
  },
  {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷',
    currency: 'ARS',
    link: 'https://www.afip.gob.ar/landing/default.asp',
  },
  {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    currency: 'ZAR',
    link: 'https://www.sars.gov.za/',
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    currency: 'NZD',
    link: 'https://www.ird.govt.nz/',
  },
  {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪',
    currency: 'EUR',
    link: 'https://www.revenue.ie/en/',
  },
  {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱',
    currency: 'PLN',
    link: 'https://www.gov.pl/web/kas',
  },
  {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹',
    currency: 'EUR',
    link: 'https://www.e-financas.gov.pt/',
  },
  {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷',
    currency: 'EUR',
    link: 'https://www.aade.gr/',
  },
  {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹',
    currency: 'EUR',
    link: 'https://www.bmf.gv.at/en',
  },
  {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷',
    currency: 'KRW',
    link: 'https://www.nts.go.kr/eng/',
  },
  {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼',
    currency: 'TWD',
    link: 'https://www.ntbt.gov.tw/',
  },
  {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾',
    currency: 'MYR',
    link: 'https://www.hasil.gov.my/en/',
  },
  {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭',
    currency: 'THB',
    link: 'https://www.rd.go.th/english',
  },
  {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩',
    currency: 'IDR',
    link: 'https://www.pajak.go.id/en',
  },
  {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭',
    currency: 'PHP',
    link: 'https://www.bir.gov.ph/',
  },
  {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳',
    currency: 'VND',
    link: 'https://www.gdt.gov.vn/',
  },
  {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷',
    currency: 'TRY',
    link: 'https://www.gib.gov.tr/en',
  },
  {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺',
    currency: 'RUB',
    link: 'https://www.nalog.gov.ru/eng/',
  },
  {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱',
    currency: 'ILS',
    link: 'https://www.gov.il/en/departments/topics/tax_authority',
  },
];

const MultiCurrency = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  const [currencyData, setCurrencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realTimeRates, setRealTimeRates] = useState(null);
  const [syncingRates, setSyncingRates] = useState(false);
  const [fetchingRealTime, setFetchingRealTime] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [taxSearchTerm, setTaxSearchTerm] = useState('');

  // Mock data for multi-currency management
  const mockCurrencyData = [
    {
      id: 'USD',
      name: 'US Dollar',
      symbol: '$',
      code: 'USD',
      rate: 1.0,
      change: 0.0,
      changePercent: 0.0,
      status: 'Base',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 15,
      totalValue: '$2,450,000',
      description: 'Base currency for all transactions',
    },
    {
      id: 'EUR',
      name: 'Euro',
      symbol: '€',
      code: 'EUR',
      rate: 0.92,
      change: 0.0025,
      changePercent: 0.27,
      status: 'Active',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 8,
      totalValue: '€1,840,000',
      description: 'European contracts and operations',
    },
    {
      id: 'GBP',
      name: 'British Pound',
      symbol: '£',
      code: 'GBP',
      rate: 0.79,
      change: -0.0015,
      changePercent: -0.19,
      status: 'Active',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 5,
      totalValue: '£1,185,000',
      description: 'UK operations and contracts',
    },
    {
      id: 'JPY',
      name: 'Japanese Yen',
      symbol: '¥',
      code: 'JPY',
      rate: 148.5,
      change: 0.25,
      changePercent: 0.17,
      status: 'Active',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 3,
      totalValue: '¥445,500,000',
      description: 'Japanese market operations',
    },
    {
      id: 'CAD',
      name: 'Canadian Dollar',
      symbol: 'C$',
      code: 'CAD',
      rate: 1.35,
      change: -0.003,
      changePercent: -0.22,
      status: 'Active',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 4,
      totalValue: 'C$1,350,000',
      description: 'Canadian operations',
    },
    {
      id: 'AUD',
      name: 'Australian Dollar',
      symbol: 'A$',
      code: 'AUD',
      rate: 1.52,
      change: 0.0045,
      changePercent: 0.3,
      status: 'Active',
      lastUpdated: '2024-01-20T10:30:00Z',
      contracts: 2,
      totalValue: 'A$760,000',
      description: 'Australian market contracts',
    },
  ];

  // Currency statistics
  const currencyStats = {
    totalCurrencies: 6,
    activeCurrencies: 5,
    baseCurrency: 'USD',
    totalContracts: 37,
    totalExposure: '$7.03M',
    lastUpdate: '2024-01-20 10:30 AM',
    rateChanges: 3,
  };

  // Load currency rates from database
  const loadCurrencyRates = async () => {
    try {
      setLoading(true);
      const response = await multiCurrencyService.getRates({ baseCurrency });
      if (response.success && response.data) {
        // Transform database rates to component format
        const transformedData = response.data.map((rate) => {
          // Find if we already have this currency in our list
          const existingCurrency = mockCurrencyData.find(
            (c) => c.code === rate.targetCurrency,
          );
          return {
            id: rate._id || rate.rateId,
            name: existingCurrency?.name || rate.targetCurrency,
            symbol: existingCurrency?.symbol || rate.targetCurrency,
            code: rate.targetCurrency,
            rate: rate.exchangeRate,
            change: 0, // Will be calculated if we have historical data
            changePercent: 0,
            status: rate.isActive ? 'Active' : 'Inactive',
            lastUpdated: rate.rateDate || rate.updatedAt,
            source: rate.source || 'Manual',
            _id: rate._id,
            rateId: rate.rateId,
          };
        });
        setCurrencyData(transformedData);
      }
    } catch (error) {
      console.error('Error loading currency rates:', error);
      // Fallback to mock data if API fails
      setCurrencyData(mockCurrencyData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrencyRates();
  }, [baseCurrency]);

  // Define columns for DataTable
  const columns = [
    {
      key: 'code',
      label: 'Currency',
      width: '120px',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={14} style={{ color: 'var(--primary-color)' }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>{value}</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>{row.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'symbol',
      label: 'Symbol',
      width: '80px',
      render: (value) => (
        <span style={{ fontSize: '16px', fontWeight: '600' }}>{value}</span>
      ),
    },
    {
      key: 'rate',
      label: 'Exchange Rate',
      width: '140px',
      render: (value, row) => (
        <div>
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            {row.code === 'USD' ? '1.0000' : value.toFixed(4)}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            {row.code === 'USD'
              ? 'Base Currency'
              : `1 USD = ${value.toFixed(4)} ${row.code}`}
          </div>
        </div>
      ),
    },
    {
      key: 'change',
      label: 'Change',
      width: '120px',
      render: (value, row) => {
        const isPositive = value >= 0;
        const isBase = row.code === 'USD';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {!isBase && (
              <>
                {isPositive ? (
                  <TrendingUp size={12} style={{ color: '#10b981' }} />
                ) : (
                  <TrendingDown size={12} style={{ color: '#ef4444' }} />
                )}
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: isPositive ? '#10b981' : '#ef4444',
                  }}
                >
                  {value > 0 ? '+' : ''}
                  {value.toFixed(4)}
                </span>
              </>
            )}
            {isBase && (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Base</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'changePercent',
      label: 'Change %',
      width: '100px',
      render: (value, row) => {
        const isPositive = value >= 0;
        const isBase = row.code === 'USD';
        return (
          <div>
            {!isBase && (
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: isPositive ? '#10b981' : '#ef4444',
                }}
              >
                {value > 0 ? '+' : ''}
                {value.toFixed(2)}%
              </span>
            )}
            {isBase && (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>0.00%</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          Base: 'badge-primary',
          Active: 'badge-success',
          Inactive: 'badge-secondary',
        };
        return statusClasses[value] || 'badge-secondary';
      },
    },
    {
      key: 'contracts',
      label: 'Contracts',
      width: '100px',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FileText size={12} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '13px', fontWeight: '500' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'totalValue',
      label: 'Total Value',
      width: '140px',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={12} style={{ color: 'var(--success-color)' }} />
          <span
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--success-color)',
            }}
          >
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => {
        if (!row || (!row.id && !row._id)) {
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <CurrencyActionDropdown
            currency={row}
            onView={handleViewCurrency}
            onEdit={handleEditCurrency}
            onDelete={handleDeleteCurrency}
          />
        );
      },
    },
  ];

  const handleEditCurrency = (currency) => {
    setSelectedCurrency(currency);
    setShowEditModal(true);
  };

  const handleViewCurrency = (currency) => {
    setSelectedCurrency(currency);
    setShowViewModal(true);
  };

  const handleDeleteCurrency = (currency) => {
    setCurrencyToDelete(currency);
    setShowDeleteModal(true);
  };

  // Confirm delete currency
  const confirmDeleteCurrency = async () => {
    if (!currencyToDelete) return;

    setIsDeleting(true);

    try {
      // Simulate API call
      setCurrencyData((prev) =>
        prev.filter((currency) => currency.id !== currencyToDelete.id),
      );
      success('Currency deleted successfully!');

      setShowDeleteModal(false);
      setCurrencyToDelete(null);
    } catch (error) {
      console.error('Error deleting currency:', error);
      showError('Failed to delete currency');
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCurrencyToDelete(null);
    setIsDeleting(false);
  };

  const handleRowClick = (currency) => {
    setSelectedCurrency(currency);
    setShowViewModal(true);
  };

  // Fetch real-time rates from API (without saving)
  const fetchRealTimeRates = async () => {
    try {
      setFetchingRealTime(true);
      const targetCurrencies = currencyData
        .filter((c) => c.code !== baseCurrency)
        .map((c) => c.code);

      const response = await multiCurrencyService.getRealTimeRates(
        baseCurrency,
        targetCurrencies.length > 0 ? targetCurrencies : [],
      );

      if (response.success && response.data) {
        setRealTimeRates(response.data);
        success('Real-time rates fetched successfully!');
      }
    } catch (error) {
      console.error('Error fetching real-time rates:', error);
      showError(
        error.error || 'Failed to fetch real-time rates. Please try again.',
      );
    } finally {
      setFetchingRealTime(false);
    }
  };

  // Sync real-time rates to database
  const handleRefreshRates = async () => {
    try {
      setSyncingRates(true);
      const targetCurrencies = currencyData
        .filter((c) => c.code !== baseCurrency)
        .map((c) => c.code);

      const response = await multiCurrencyService.syncRealTimeRates(
        baseCurrency,
        targetCurrencies.length > 0 ? targetCurrencies : [],
      );

      if (response.success && response.data) {
        success(
          `Successfully synced ${response.data.syncedCount} currency rate(s)!`,
        );
        // Reload rates from database
        await loadCurrencyRates();
      }
    } catch (error) {
      console.error('Error syncing rates:', error);
      showError(error.error || 'Failed to sync rates. Please try again.');
    } finally {
      setSyncingRates(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/multi-currency';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleNewCurrency = () => {
    setShowAddModal(true);
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'New Currency',
      icon: <Plus size={18} />,
      onClick: handleNewCurrency,
      variant: 'primary',
      tooltip: 'Add new currency',
    },
  ];

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinning { animation: spin 1s linear infinite; }

        /* Centered, polished modal styles */
        .custom-modal-overlay {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 200000;
          background: rgba(0,0,0,0.45);
          -webkit-backdrop-filter: blur(3px);
          backdrop-filter: blur(3px);
        }

        .custom-modal-dialog {
          width: 100%;
          max-width: 920px;
          margin: 0;
        }

        .custom-modal-content {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(2,6,23,0.18);
          background-color: #fff;
          position: relative;
          border: 1px solid rgba(15, 23, 42, 0.06);
          display: flex;
          flex-direction: column;
          max-height: 80vh;
        }

        .custom-modal-header {
          background-color: var(--primary-color);
          color: white;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .custom-modal-body {
          padding: 20px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .custom-modal-close {
          appearance: none;
          -webkit-appearance: none;
          border: none;
          background: rgba(255,255,255,0.12);
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .custom-modal-dialog { max-width: 95%; }
          .custom-modal-body { padding: 16px; }
        }
      `}</style>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Multi-Currency"
        description="Manage global contracts and currency exchange rates"
        actions={headerActions}
      />
      <div className="page-container">
        {/* Tabbed Navigation */}
        <div style={{ marginBottom: '32px' }}>
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid #e5e7eb',
              gap: '0',
            }}
          >
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color:
                  activeTab === 'overview' ? 'var(--primary-color)' : '#6b7280',
                borderBottom:
                  activeTab === 'overview'
                    ? '3px solid var(--primary-color)'
                    : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px',
              }}
              onClick={() => setActiveTab('overview')}
            >
              <BarChart3
                size={18}
                style={{
                  color:
                    activeTab === 'overview'
                      ? 'var(--primary-color)'
                      : '#9ca3af',
                }}
              />
              Currency Overview
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color:
                  activeTab === 'rates' ? 'var(--primary-color)' : '#6b7280',
                borderBottom:
                  activeTab === 'rates'
                    ? '3px solid var(--primary-color)'
                    : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px',
              }}
              onClick={() => setActiveTab('rates')}
            >
              <TrendingUp
                size={18}
                style={{
                  color:
                    activeTab === 'rates' ? 'var(--primary-color)' : '#9ca3af',
                }}
              />
              Exchange Rates
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color:
                  activeTab === 'hedging' ? 'var(--primary-color)' : '#6b7280',
                borderBottom:
                  activeTab === 'hedging'
                    ? '3px solid var(--primary-color)'
                    : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px',
              }}
              onClick={() => setActiveTab('hedging')}
            >
              <Shield
                size={18}
                style={{
                  color:
                    activeTab === 'hedging'
                      ? 'var(--primary-color)'
                      : '#9ca3af',
                }}
              />
              Hedging Strategies
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color:
                  activeTab === 'compliance'
                    ? 'var(--primary-color)'
                    : '#6b7280',
                borderBottom:
                  activeTab === 'compliance'
                    ? '3px solid var(--primary-color)'
                    : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px',
              }}
              onClick={() => setActiveTab('compliance')}
            >
              <CheckCircle
                size={18}
                style={{
                  color:
                    activeTab === 'compliance'
                      ? 'var(--primary-color)'
                      : '#9ca3af',
                }}
              />
              Tax Compliance
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: activeTab === 'ai-insights' ? '#8B5CF6' : '#6b7280',
                borderBottom:
                  activeTab === 'ai-insights'
                    ? '3px solid #8B5CF6'
                    : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px',
              }}
              onClick={() => setActiveTab('ai-insights')}
            >
              <Brain
                size={18}
                style={{
                  color: activeTab === 'ai-insights' ? '#8B5CF6' : '#9ca3af',
                }}
              />
              AI Insights
            </button>
          </div>
        </div>

        {/* KPI Cards Grid - Matching Dashboard styling */}
        <div
          className="grid-modern-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          {/* Active Currencies */}
          <div className="asset-landing-card">
            <div
              style={{
                height: '4px',
                background: `var(--primary-color)`,
                width: '100%',
              }}
            />
            <div style={{ padding: '24px' }}>
              <div className="d-flex align-items-center mb-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#D6EFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                  }}
                >
                  <div style={{ color: 'var(--gold-primary)' }} className="asset-card-icon">
                    <Globe size={24} />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#059669',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    <ArrowUpRight size={16} />
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>
                      +{currencyStats.totalCurrencies}
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  marginBottom: '4px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.2',
                }}
              >
                {currencyStats.totalCurrencies}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: '500',
                  marginBottom: '4px',
                }}
              >
                Active Currencies
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {currencyStats.activeCurrencies} active
              </div>
            </div>
          </div>

          {/* Multi-Currency Contracts */}
          <div className="asset-landing-card">
            <div
              style={{
                height: '4px',
                background: `var(--primary-color)`,
                width: '100%',
              }}
            />
            <div style={{ padding: '24px' }}>
              <div className="d-flex align-items-center mb-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#D6EFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                  }}
                >
                  <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                    <FileText size={24} />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#059669',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    <ArrowUpRight size={16} />
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>
                      +{currencyStats.totalContracts}
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#10b981',
                  marginBottom: '4px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.2',
                }}
              >
                {currencyStats.totalContracts}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: '500',
                  marginBottom: '4px',
                }}
              >
                Multi-Currency Contracts
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Across {currencyStats.totalCurrencies} currencies
              </div>
            </div>
          </div>

          {/* Rate Changes Today */}
          <div className="asset-landing-card">
            <div
              style={{
                height: '4px',
                background: `var(--primary-color)`,
                width: '100%',
              }}
            />
            <div style={{ padding: '24px' }}>
              <div className="d-flex align-items-center mb-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#D6EFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                  }}
                >
                  <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                    <TrendingUp size={24} />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div
                    style={{
                      padding: '4px 10px',
                      backgroundColor: '#FEF3C7',
                      color: '#92400E',
                      borderRadius: 'var(--button-radius)',
                      fontSize: '11px',
                      fontWeight: '600',
                      fontFamily: 'var(--font-family)',
                      display: 'inline-block',
                    }}
                  >
                    Today
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#f59e0b',
                  marginBottom: '4px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.2',
                }}
              >
                {currencyStats.rateChanges}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: '500',
                  marginBottom: '4px',
                }}
              >
                Rate Changes Today
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Exchange rate updates
              </div>
            </div>
          </div>

          {/* Total Exposure */}
          <div className="asset-landing-card">
            <div
              style={{
                height: '4px',
                background: `var(--primary-color)`,
                width: '100%',
              }}
            />
            <div style={{ padding: '24px' }}>
              <div className="d-flex align-items-center mb-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#D6EFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                  }}
                >
                  <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                    <DollarSign size={24} />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#059669',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    <ArrowUpRight size={16} />
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>
                      Monitor
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  marginBottom: '4px',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.2',
                }}
              >
                {currencyStats.totalExposure}
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: '500',
                  marginBottom: '4px',
                }}
              >
                Total Exposure
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Multi-currency exposure
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-12" style={{ overflowX: 'hidden' }}>
            {activeTab === 'overview' && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <h5 style={{ margin: 0, color: '#374151' }}>
                    Currency Management
                  </h5>
                  <button
                    className="btn"
                    style={{
                      background: 'var(--primary-color)',
                      border: 'none',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '500',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onClick={handleRefreshRates}
                  >
                    <RefreshCw size={14} />
                    Refresh Rates
                  </button>
                </div>
                <DataTable
                  data={currencyData}
                  columns={columns}
                  title="Multi-Currency Overview"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={false}
                  showCheckboxes={false}
                  onRowClick={handleRowClick}
                  searchPlaceholder="Search currencies by code, name, or symbol..."
                  loading={loading}
                  emptyMessage="No currencies found. Click 'Add Currency' to get started."
                  className="shadow-lg"
                />
              </div>
            )}

            {activeTab === 'rates' && (
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background:
                      'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
                    color: 'white',
                    borderRadius: '16px 16px 0 0',
                    border: 'none',
                    padding: '20px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <TrendingUp size={20} />
                    Real-Time Exchange Rates
                  </h5>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                      onClick={fetchRealTimeRates}
                      disabled={fetchingRealTime}
                    >
                      <RefreshCw
                        size={14}
                        className={fetchingRealTime ? 'spinning' : ''}
                      />
                      {fetchingRealTime ? 'Fetching...' : 'Fetch Rates'}
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: 'none',
                        color: 'var(--primary-color)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: '600',
                      }}
                      onClick={handleRefreshRates}
                      disabled={syncingRates}
                    >
                      <RefreshCw
                        size={14}
                        className={syncingRates ? 'spinning' : ''}
                      />
                      {syncingRates ? 'Syncing...' : 'Sync to Database'}
                    </button>
                  </div>
                </div>
                <div className="card-body p-4">
                  {realTimeRates ? (
                    <div>
                      <div
                        style={{
                          marginBottom: '24px',
                          padding: '16px',
                          background: '#f0f9ff',
                          borderRadius: '8px',
                          border: '1px solid #bae6fd',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: '14px',
                                color: '#0369a1',
                                fontWeight: '600',
                                marginBottom: '4px',
                              }}
                            >
                              Base Currency: {realTimeRates.baseCurrency}
                            </div>
                            <div style={{ fontSize: '12px', color: '#0284c7' }}>
                              Last Updated:{' '}
                              {new Date(
                                realTimeRates.timestamp,
                              ).toLocaleString()}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: '4px 12px',
                              background: '#10b981',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            LIVE
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: '16px',
                        }}
                      >
                        {Object.entries(realTimeRates.rates || {}).map(
                          ([currency, rate]) => {
                            const existingCurrency =
                              currencyData.find((c) => c.code === currency) ||
                              mockCurrencyData.find((c) => c.code === currency);
                            const previousRate = currencyData.find(
                              (c) => c.code === currency,
                            )?.rate;
                            const change = previousRate
                              ? rate - previousRate
                              : 0;
                            const changePercent = previousRate
                              ? (change / previousRate) * 100
                              : 0;
                            const isPositive = change >= 0;

                            return (
                              <div
                                key={currency}
                                style={{
                                  padding: '16px',
                                  background: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#374151',
                                      }}
                                    >
                                      {currency}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#6b7280',
                                      }}
                                    >
                                      {existingCurrency?.name || currency}
                                    </div>
                                  </div>
                                  {change !== 0 && (
                                    <div
                                      style={{
                                        fontSize: '14px',
                                        color: isPositive
                                          ? '#10b981'
                                          : '#ef4444',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                      }}
                                    >
                                      {isPositive ? (
                                        <TrendingUp size={14} />
                                      ) : (
                                        <TrendingDown size={14} />
                                      )}
                                      {changePercent > 0 ? '+' : ''}
                                      {changePercent.toFixed(2)}%
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#111827',
                                    marginTop: '8px',
                                  }}
                                >
                                  {rate.toFixed(4)}
                                </div>
                                {previousRate && (
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#9ca3af',
                                      marginTop: '4px',
                                    }}
                                  >
                                    Previous: {previousRate.toFixed(4)}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                        📈
                      </div>
                      <h4 style={{ color: '#374151', marginBottom: '8px' }}>
                        Live Exchange Rates
                      </h4>
                      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                        Click "Fetch Rates" to get real-time exchange rates from
                        the API. Then click "Sync to Database" to save them to
                        your system.
                      </p>
                      <button
                        className="btn"
                        style={{
                          background: 'var(--primary-color)',
                          border: 'none',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          fontWeight: '600',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          margin: '0 auto',
                        }}
                        onClick={fetchRealTimeRates}
                        disabled={fetchingRealTime}
                      >
                        <RefreshCw
                          size={16}
                          className={fetchingRealTime ? 'spinning' : ''}
                        />
                        {fetchingRealTime
                          ? 'Fetching Rates...'
                          : 'Fetch Real-Time Rates'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'hedging' && (
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background:
                      'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
                    color: 'white',
                    borderRadius: '16px 16px 0 0',
                    border: 'none',
                    padding: '20px 24px',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <Shield size={20} />
                    Currency Hedging Strategies
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                      🛡️
                    </div>
                    <h4 style={{ color: '#374151', marginBottom: '8px' }}>
                      Risk Hedging
                    </h4>
                    <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                      Advanced currency hedging strategies and risk management
                      tools coming soon
                    </p>
                    <button
                      className="btn"
                      style={{
                        background: 'var(--primary-color)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontWeight: '600',
                        fontSize: '14px',
                      }}
                    >
                      <Shield size={16} className="me-2" />
                      Setup Hedging Strategy
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'compliance' && (
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background:
                      'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
                    color: 'white',
                    borderRadius: '16px 16px 0 0',
                    border: 'none',
                    padding: '20px 24px',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <CheckCircle size={20} />
                    Tax & Compliance Management
                  </h5>
                </div>
                <div className="card-body p-4">
                  {/* Search Bar */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ position: 'relative' }}>
                      <Search
                        size={20}
                        style={{
                          position: 'absolute',
                          left: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6b7280',
                        }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search for tax rules by country name or currency..."
                        value={taxSearchTerm}
                        onChange={(e) => setTaxSearchTerm(e.target.value)}
                        style={{
                          paddingLeft: '48px',
                          paddingRight: '16px',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          fontSize: '14px',
                          width: '100%',
                        }}
                      />
                    </div>
                  </div>

                  {/* Countries Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '16px',
                      maxHeight: '600px',
                      overflowY: 'auto',
                      padding: '8px',
                    }}
                  >
                    {TAX_RULES_DATA.filter(
                      (country) =>
                        !taxSearchTerm ||
                        country.name
                          .toLowerCase()
                          .includes(taxSearchTerm.toLowerCase()) ||
                        country.code
                          .toLowerCase()
                          .includes(taxSearchTerm.toLowerCase()) ||
                        country.currency
                          .toLowerCase()
                          .includes(taxSearchTerm.toLowerCase()),
                    ).map((country) => (
                      <a
                        key={country.code}
                        href={country.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                        }}
                      >
                        <div
                          style={{
                            padding: '16px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              'var(--primary-color)';
                            e.currentTarget.style.boxShadow =
                              '0 4px 12px rgba(0, 123, 143, 0.15)';
                            e.currentTarget.style.transform =
                              'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* Country Flag */}
                          <div
                            style={{
                              fontSize: '32px',
                              lineHeight: '1',
                              flexShrink: 0,
                            }}
                          >
                            {country.flag}
                          </div>

                          {/* Country Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#111827',
                                marginBottom: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {country.name}
                            </div>
                            <div
                              style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <span>{country.currency}</span>
                              <span>•</span>
                              <span>{country.code}</span>
                            </div>
                          </div>

                          {/* External Link Icon */}
                          <ArrowUpRight
                            size={18}
                            style={{
                              color: '#6b7280',
                              flexShrink: 0,
                            }}
                          />
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* Empty State */}
                  {TAX_RULES_DATA.filter(
                    (country) =>
                      !taxSearchTerm ||
                      country.name
                        .toLowerCase()
                        .includes(taxSearchTerm.toLowerCase()) ||
                      country.code
                        .toLowerCase()
                        .includes(taxSearchTerm.toLowerCase()) ||
                      country.currency
                        .toLowerCase()
                        .includes(taxSearchTerm.toLowerCase()),
                  ).length === 0 && (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#6b7280',
                        }}
                      >
                        <Search
                          size={48}
                          style={{ marginBottom: '16px', opacity: 0.5 }}
                        />
                        <h5 style={{ color: '#374151', marginBottom: '8px' }}>
                          No results found
                        </h5>
                        <p>Try searching with a different term</p>
                      </div>
                    )}

                  {/* Info Text */}
                  <div
                    style={{
                      marginTop: '24px',
                      padding: '12px 16px',
                      background: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #bae6fd',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#0369a1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckCircle size={16} />
                      Click on any country to view official tax rules and
                      compliance information
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ai-insights' && (
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background:
                      'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
                    color: 'white',
                    borderRadius: '16px 16px 0 0',
                    border: 'none',
                    padding: '20px 24px',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <Brain size={20} />
                    AI-Powered Currency Insights
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                      🤖
                    </div>
                    <h4 style={{ color: '#374151', marginBottom: '8px' }}>
                      AI Currency Analysis
                    </h4>
                    <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                      Advanced AI-powered currency trend analysis and predictive
                      insights coming soon
                    </p>
                    <button
                      className="btn"
                      style={{
                        background:
                          'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontWeight: '600',
                        fontSize: '14px',
                      }}
                    >
                      <Brain size={16} className="me-2" />
                      Get AI Insights
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* View Currency Modal */}
        {showViewModal && selectedCurrency && (
          <div className="custom-modal-overlay" role="dialog" aria-modal="true">
            <div className="custom-modal-dialog">
              <div className="custom-modal-content">
                <div className="custom-modal-header">
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <Eye size={20} />
                    <h5 style={{ margin: 0, fontFamily: 'var(--font-family)' }}>
                      Currency Details
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="custom-modal-close"
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedCurrency(null);
                    }}
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>

                <div className="custom-modal-body">
                  <CurrencyViewDetails currency={selectedCurrency} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDeleteCurrency}
          title="Delete Currency"
          message={`Are you sure you want to delete "${currencyToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

// Currency View Details Component
const CurrencyViewDetails = ({ currency }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div style={{ fontFamily: 'var(--font-family)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Top Row */}
        <div style={{ display: 'flex', gap: '16px', minHeight: '200px' }}>
          {/* Currency Overview */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card h-100">
              <div
                className="card-header py-2"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  💱 Currency Overview
                </h6>
              </div>
              <div className="card-body py-3">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Code
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.code || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Name
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.name || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Symbol
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        color: '#212529',
                        fontWeight: '600',
                      }}
                    >
                      {currency?.symbol || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Status
                    </div>
                    <div>
                      <span
                        className={`badge badge-sm ${currency?.status === 'Base' ? 'bg-primary' : currency?.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}
                        style={{ fontSize: '11px' }}
                      >
                        {currency?.status || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Exchange Rate
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.code === 'USD'
                        ? '1.0000 (Base)'
                        : currency?.rate?.toFixed(4) || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Change
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: currency?.change >= 0 ? '#10b981' : '#ef4444',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.code === 'USD'
                        ? '0.0000'
                        : (currency?.change > 0 ? '+' : '') +
                        (currency?.change?.toFixed(4) || '0.0000')}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Change %
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color:
                          currency?.changePercent >= 0 ? '#10b981' : '#ef4444',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.code === 'USD'
                        ? '0.00%'
                        : (currency?.changePercent > 0 ? '+' : '') +
                        (currency?.changePercent?.toFixed(2) || '0.00') +
                        '%'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Last Updated
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {formatDate(currency?.lastUpdated)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Impact */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card h-100">
              <div
                className="card-header py-2"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  💰 Financial Impact
                </h6>
              </div>
              <div className="card-body py-3">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Active Contracts
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {currency?.contracts || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Total Value
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#10b981',
                        fontWeight: '600',
                      }}
                    >
                      {currency?.totalValue || 'N/A'}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                        marginBottom: '4px',
                      }}
                    >
                      Description
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {currency?.description || 'No description available'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiCurrency;
